/**
 * State that autogenerated elevation annotations depend on.
 * Comparing these state objects determines when elevation annotation preview needs to be updated.
 * It doesn't cover state that can be edited interactively in elevation preview.
 * @module
 */
import { groundLevel } from "building-generation/elements/level/levelTools";
import { getRoomSubRoofs } from "building-generation/elements/roofs";
import { getEaveDepth } from "building-generation/elements/roofTrimUtility";
import { checkWallRooms } from "building-generation/elements/walls/wall";
import { iterateBuildingGeometries } from "building-geometry/geometry/buildingGeometry";
import type { Building } from "building-types/building/buildingTypes";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { EdgeElevationNote } from "building-types/edgeBehavior/edgeBehavior";
import type { FacadeSweepStratum } from "building-types/facades/facadeTypes";
import { lineRenderGeometryOptionsNew } from "building-types/geometry/buildingGeometryTypes";
import type { FaceIteratorParams } from "building-types/geometry/faceIteratorTypes";
import type { Level } from "building-types/levels/levelTypes";
import { wallFaceCategories, FaceCategory } from "building-types/materials/faceCategories";
import type { Prop } from "building-types/props/propTypes";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { DormerElevationPlanSettings, RoofElevationPlanSettings } from "building-types/settings/roofPlanSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { ElevationNotePlacementType } from "building-types/settings/settingsTypesEnum";
import type { ElevationNote, ElevationNoteTable } from "building-types/viewNotes/elevationNoteTypes";
import { WallType } from "building-types/walls/wallEnums";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { Table } from "common/data/table";
import { compareArrays } from "common/utils/arrayCompare";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import type { Face } from "geom/brep";
import { planeIsOrientedCoplanar } from "geom/plane";
import { vec2DistSquared } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3DistSquared } from "geom/vec3";
import {
  elevationViewPlaneFromEyePos,
  getUniqueFloorAndCeilingOffsets,
} from "lib/house/plans/generateElevationSheetGeometry";

type FaceGroups = { [groupType: number]: Face[] };

// Height values that are relevant for Portico elevation annotations, recorded in a flat array.
type PorticoState = number[];

interface AnnotationLevel {
  floorElevation: number;
  topOfWall: number;
  windowHeadHeight: number;
  floorOffsets: number[];
  ceilingOffsets: number[];
  porchDatum: number;
}

type ElevationNoteState = Omit<ElevationNote, "roomId" | "elevationNote"> & Omit<EdgeElevationNote, "id">;

export interface ElevationAnnotationBuildingState {
  faceGroups: FaceGroups;
  levels: AnnotationLevel[];
  porticos: PorticoState;
  elevationNotes: ElevationNoteState[];
  dormerInstanceElevationSettings: DormerElevationPlanSettings[];
  roofInstanceElevationSettings: RoofElevationPlanSettings[];
  enableAutogradeLine: boolean;
  showWindowHeaderDimsOnElevations: boolean;
  automatedDatumAnnotations: boolean;
  elevationNotePlacementType: ElevationNotePlacementType;
  buildingDimensionWidth: number;
  buildingDimensionDepth: number;
  facadeSweepStrata: Table<FacadeSweepStratum>;
  exteriorTrimSettings: ExteriorTrimSettings;
  props: Table<Prop>;
}

interface GetPorticoStateOptions {
  eyePos: Vec3;
  levels: Table<Level>;
  rooms: Table<Room>;
  walls: Building["walls"];
  roofs: Table<GeneratedRoofSystem>;
  roofSettings: RoofSettings;
}

export function getPorticoTopHeight(
  room: Room,
  topOfWall: number,
  roofs: Table<GeneratedRoofSystem>,
  roofSettings: RoofSettings,
) {
  const { ceilingHeightOffset = 0 } = room;
  let porticoTopHeight: number | undefined;
  // get the top height from the portico sub roofs
  const porticoRoofs = getRoomSubRoofs(room, roofs);
  for (const porticoRoof of porticoRoofs) {
    porticoTopHeight = Math.max(
      porticoTopHeight ?? 0,
      porticoRoof.roofVolume.baseHeight - getEaveDepth(roofSettings) - GABLE_ROOF_THICKNESS,
    );
  }

  // if no roof is found use the ceiling height
  const porticoCeilingHeight: number = topOfWall + ceilingHeightOffset;
  return porticoTopHeight ?? porticoCeilingHeight;
}

function getPorticoState({ eyePos, levels, rooms, walls, roofs, roofSettings }: GetPorticoStateOptions): PorticoState {
  const porticoState: number[] = [];
  const groundLev = groundLevel(levels);
  if (!groundLev) {
    return porticoState;
  }

  if (eyePos[1] < 0) {
    const { floorElevation, topOfWall } = groundLev;

    for (const roomId in rooms) {
      const room = rooms[roomId];
      const {
        type,
        level: { id: levelId },
      } = room;

      if (type !== RoomType.PORTICO) {
        continue;
      }

      const wallIndex = walls[levelId].wallIndex;
      const roomWalls = wallIndex.wallsInRoom(room);

      const porticoTopHeight: number = getPorticoTopHeight(room, topOfWall, roofs, roofSettings);

      // look for the portico door
      for (const wall of roomWalls) {
        if (wall.wallType !== WallType.SOLID) {
          continue;
        }

        const isExteriorWall = checkWallRooms(wall, (r) => {
          return !r || !isWalledRoomEnclosure[r.attributes.roomEnclosure];
        });

        if (!isExteriorWall) {
          continue;
        }

        const { doorPlacements } = wall;

        for (const door of doorPlacements) {
          const { springHeight, descriptor } = door;

          if (descriptor.operationType === DoorOperationType.ARC) {
            porticoState.push(porticoTopHeight + floorElevation);
            porticoState.push(descriptor.height + floorElevation);
          }
          if (springHeight !== undefined) {
            porticoState.push(springHeight);
          }
        }
      }
    }
  }
  return porticoState;
}

function isPorticoStateEqual(porticosA: PorticoState, porticosB: PorticoState) {
  return compareArrays(porticosA, porticosB, (a, b) => a === b);
}

function getLevelsState(building: Building, eyePos: Vec3) {
  const viewPlane = elevationViewPlaneFromEyePos(eyePos);
  return Object.keys(building.levels).map((key) => {
    const level = building.levels[key];
    const levelRooms = roomsOnLevel(level.id, building.rooms, building.roomsByLevel);
    const { floorOffsets, ceilingOffsets } = getUniqueFloorAndCeilingOffsets(levelRooms, building, viewPlane);
    return {
      floorElevation: level.floorElevation,
      topOfWall: level.topOfWall,
      windowHeadHeight: building.levelsAttributes[level.id]?.windowHeadHeight ?? 0,
      floorOffsets,
      ceilingOffsets,
      porchDatum: level.levelDatumsGenerated.porch,
    };
  });
}

function isLevelsStateEqual(levelsA: AnnotationLevel[], levelsB: AnnotationLevel[]): boolean {
  if (levelsA.length !== levelsB.length) {
    return false;
  }

  for (let i = 0, il = levelsA.length; i < il; ++i) {
    const levelA = levelsA[i];
    const levelB = levelsB[i];
    if (levelA.floorElevation !== levelB.floorElevation) {
      return false;
    }
    if (levelA.topOfWall !== levelB.topOfWall) {
      return false;
    }
    if (levelA.windowHeadHeight !== levelB.windowHeadHeight) {
      return false;
    }
    if (levelA.porchDatum !== levelB.porchDatum) {
      return false;
    }
    if (levelA.floorOffsets.length !== levelB.floorOffsets.length) {
      return false;
    }
    if (levelA.ceilingOffsets.length !== levelB.ceilingOffsets.length) {
      return false;
    }
    for (let j = 0, jl = levelA.floorOffsets.length; j < jl; ++j) {
      if (levelA.floorOffsets[j] !== levelB.floorOffsets[j]) {
        return false;
      }
    }
    for (let j = 0, jl = levelB.ceilingOffsets.length; j < jl; ++j) {
      if (levelA.ceilingOffsets[j] !== levelB.ceilingOffsets[j]) {
        return false;
      }
    }
  }
  return true;
}

function getElevationNoteState(elevationNotes: ElevationNoteTable): ElevationNoteState[] {
  const notes: ElevationNoteState[] = [];
  for (const elevationNoteId in elevationNotes) {
    const { elevationNote, x, y, z, nx, ny, isExteriorWall } = elevationNotes[elevationNoteId];
    const {
      text,
      offset,
      edgeId,
      param,
      paramNormalized,
      paramAnchor,
      mirrorWithBuilding,
      rotation,
      datumId,
      disabled,
    } = elevationNote;
    notes.push({
      x,
      y,
      z,
      nx,
      ny,
      isExteriorWall,
      text,
      offset,
      edgeId,
      param,
      paramNormalized,
      paramAnchor,
      mirrorWithBuilding,
      rotation,
      datumId,
      disabled,
    });
  }
  return notes;
}

function isElevationNotesStateEqual(elevationNotesA: ElevationNoteState[], elevationNotesB: ElevationNoteState[]) {
  if (elevationNotesA.length !== elevationNotesB.length) {
    return false;
  }
  for (let i = 0; i < elevationNotesA.length; ++i) {
    const noteA = elevationNotesA[i];
    const noteB = elevationNotesB[i];
    for (const key in noteA) {
      if (noteA[key] !== noteB[key]) {
        return false;
      }
    }
  }
  return true;
}

function getFacesState(building: Building): FaceGroups {
  const faceGroups: FaceGroups = {};
  function iterateFace({ faceCategory, faces }: FaceIteratorParams) {
    if (faceGroups[faceCategory] === undefined) {
      faceGroups[faceCategory] = [];
    }
    faceGroups[faceCategory].push(...faces);
  }
  // We're only gathering roofs and walls here, since we want a lightweight check.
  // If for example doors or windows change, the wall faces will change as well.
  iterateBuildingGeometries(
    building,
    new Set<FaceCategory>([...wallFaceCategories, FaceCategory.ROOF_TOP]),
    lineRenderGeometryOptionsNew(false, undefined),
    iterateFace,
  );
  return faceGroups;
}

function isFacesStateEqual(faceGroupsA: FaceGroups, faceGroupsB: FaceGroups): boolean {
  for (const faceGroupAKey of Object.keys(faceGroupsA)) {
    if (faceGroupsB[faceGroupAKey] === undefined) {
      return false;
    }
    if (faceGroupsB[faceGroupAKey].length !== faceGroupsA[faceGroupAKey].length) {
      return false;
    }
  }
  for (const faceGroupBKey of Object.keys(faceGroupsB)) {
    if (faceGroupsA[faceGroupBKey] === undefined) {
      return false;
    }
  }
  for (const faceGroupAKey of Object.keys(faceGroupsA)) {
    const faceGroupA: Face[] = faceGroupsA[faceGroupAKey];
    const faceGroupB: Face[] = faceGroupsB[faceGroupAKey];
    for (let i = 0, il = faceGroupA.length; i < il; ++i) {
      const faceA = faceGroupA[i];
      const faceB = faceGroupB[i];
      if (!planeIsOrientedCoplanar(faceA.plane, faceB.plane)) {
        return false;
      }
      // Here we want a lightweight check and it doesn't matter too much if it occasionally returns false negatives, so we'll just ensure that the planes have the same origin.
      if (
        vec3DistSquared(
          faceA.plane.originX,
          faceA.plane.originY,
          faceA.plane.originZ,
          faceB.plane.originX,
          faceB.plane.originY,
          faceB.plane.originZ,
        ) > 0.01
      ) {
        return false;
      }
      if (faceA.loops.length !== faceB.loops.length) {
        return false;
      }
      for (let j = 0, jl = faceA.loops.length; j < jl; ++j) {
        const loopA = faceA.loops[j];
        const loopB = faceB.loops[j];
        if (loopA.length !== loopB.length) {
          return false;
        }
        for (let k = 0, kl = loopA.length / 2; k < kl; ++k) {
          if (vec2DistSquared(loopA[k * 2], loopA[k * 2 + 1], loopB[k * 2], loopB[k * 2 + 1]) > 0.01) {
            return false;
          }
        }
      }
    }
  }
  return true;
}

// strictly for collecting the props of RoofElevationPlanSettings
const roofElevationPlanSettingsProps: Required<RoofElevationPlanSettings> = {
  showRoofHorizontalPlacementDims: true,
  showRoofGableWindowVerticalDims: true,
  showRoofGableWindowReportedName: true,
  showRoofTruncationHeightDims: true,
  showRoofWidthDims: true,
  showRoofSlopeElevationAnnotation: true,

  showDormerWindowVerticalDimensions: true,
  showDormerWindowReportedName: true,
  showDormerWallFramingHeightDimensions: true,
  showDormerWallFramingWidthDimensions: true,
  showDormerGableWindowVerticalDims: true,
  showDormerGableWindowReportedName: true,
  showDormerRoofTruncationHeightDims: true,
  showDormerRoofWidthDims: true,
  showDormerHorizontalPlacementDims: true,
  showDormerRoofSlopeElevationAnnotation: true,
};

const roofElevationPlanSettingsKeys = Object.keys(roofElevationPlanSettingsProps);

function isRoofElevationPlanSettingsEqual(
  settingsA: RoofElevationPlanSettings,
  settingsB: RoofElevationPlanSettings,
): boolean {
  for (const key of roofElevationPlanSettingsKeys) {
    if (settingsA[key] !== settingsB[key]) {
      return false;
    }
  }
  return true;
}

/**
 * Generate state that's used to decide whether we will trigger a full elevation annotation update or not.
 * We try to make this step as lightweight as possible, since it runs on the main thread.
 * @param building
 * @param viewMode
 * @returns
 */
export function elevationAnnotationBuildingStateFromBuilding(
  building: Building,
  eyePos: Vec3,
): ElevationAnnotationBuildingState {
  // We could potentially leverage object identity more here, rather than doing more detailed comparisons.
  // However, that could result in extra updates - sometimes dependencies of some of the building data could change
  // and cause the object to be refreshed even if it doesn't actually change.
  const buildingState: ElevationAnnotationBuildingState = {
    elevationNotes: getElevationNoteState(building.elevationNotes),
    faceGroups: getFacesState(building),
    levels: getLevelsState(building, eyePos),
    porticos: getPorticoState({
      eyePos,
      levels: building.levels,
      rooms: building.rooms,
      walls: building.walls,
      roofs: building.roofSystems,
      roofSettings: building.settings.roof,
    }),
    dormerInstanceElevationSettings: [],
    roofInstanceElevationSettings: [],
    enableAutogradeLine: building.settings.planset.enableAutogradeLine,
    showWindowHeaderDimsOnElevations: building.settings.planset.showWindowHeaderDimsOnElevations,
    automatedDatumAnnotations: building.settings.planset.automatedDatumAnnotations,
    elevationNotePlacementType: building.settings.planset.elevationNotePlacementType,
    buildingDimensionWidth: building.buildingBounds.width,
    buildingDimensionDepth: building.buildingBounds.depth,
    facadeSweepStrata: building.facadeSweepStrata,
    exteriorTrimSettings: building.settings.exteriorTrim,
    props: building.props,
  };

  for (const roofId in building.roofSystems) {
    const roofSystem = building.roofSystems[roofId];
    const { roofs } = roofSystem;

    for (const roofId in roofs) {
      const subRoof = roofs[roofId];
      const { isDormer } = subRoof;
      if (subRoof.roofElevationPlanSettings) {
        if (!isDormer) {
          buildingState.roofInstanceElevationSettings.push(subRoof.roofElevationPlanSettings);
        } else {
          buildingState.dormerInstanceElevationSettings.push(subRoof.roofElevationPlanSettings);
        }
      }
    }
  }

  return buildingState;
}

export function elevationAnnotationBuildingStateEqual(
  stateA: ElevationAnnotationBuildingState,
  stateB: ElevationAnnotationBuildingState,
): boolean {
  // Shortcut for undefined states
  if (stateA === undefined || stateB === undefined) {
    return false;
  }

  if (stateA.enableAutogradeLine !== stateB.enableAutogradeLine) {
    return false;
  }

  if (stateA.showWindowHeaderDimsOnElevations !== stateB.showWindowHeaderDimsOnElevations) {
    return false;
  }

  if (stateA.automatedDatumAnnotations !== stateB.automatedDatumAnnotations) {
    return false;
  }

  if (stateA.elevationNotePlacementType !== stateB.elevationNotePlacementType) {
    return false;
  }

  if (stateA.buildingDimensionWidth !== stateB.buildingDimensionWidth) {
    return false;
  }
  if (stateA.buildingDimensionDepth !== stateB.buildingDimensionDepth) {
    return false;
  }

  if (stateA.dormerInstanceElevationSettings.length !== stateB.dormerInstanceElevationSettings.length) {
    return false;
  }
  for (let i = 0, il = stateA.dormerInstanceElevationSettings.length; i < il; ++i) {
    if (
      !isRoofElevationPlanSettingsEqual(
        stateA.dormerInstanceElevationSettings[i],
        stateB.dormerInstanceElevationSettings[i],
      )
    ) {
      return false;
    }
  }

  if (stateA.roofInstanceElevationSettings.length !== stateB.roofInstanceElevationSettings.length) {
    return false;
  }
  for (let i = 0, il = stateA.roofInstanceElevationSettings.length; i < il; ++i) {
    if (
      !isRoofElevationPlanSettingsEqual(
        stateA.roofInstanceElevationSettings[i],
        stateB.roofInstanceElevationSettings[i],
      )
    ) {
      return false;
    }
  }

  if (!isLevelsStateEqual(stateA.levels, stateB.levels)) {
    return false;
  }

  if (!isElevationNotesStateEqual(stateA.elevationNotes, stateB.elevationNotes)) {
    return false;
  }

  if (!isPorticoStateEqual(stateA.porticos, stateB.porticos)) {
    return false;
  }

  if (!isFacesStateEqual(stateA.faceGroups, stateB.faceGroups)) {
    return false;
  }

  if (stateA.facadeSweepStrata !== stateB.facadeSweepStrata) {
    return false;
  }

  if (stateA.exteriorTrimSettings !== stateB.exteriorTrimSettings) {
    return false;
  }

  if (stateA.props !== stateB.props) {
    return false;
  }
  return true;
}
